/**
 * QUE UN PESO SEA UN PESO (#48 del informe del 15-08).
 *
 * Lo que encontró Jesús en su histórico: «77,1 → 94 → 75 → 94 → 50 kg, saltos de 44 kg
 * entre ajustes seguidos. Sin validación ni aviso». De ese número salen la gráfica, el
 * ritmo de cambio, el ajuste del mes y lo que lee el asistente, así que un dedazo no se
 * queda quieto en su fila: mueve el método entero.
 *
 * Dos filtros, y a propósito no son el mismo:
 *
 *   - EL RANGO. Fuera de 25-300 kg no es un peso, es una errata (o los gramos). Se rechaza
 *     y se deja lo que hubiera. Es el mismo rango que valida el servidor en el reporte y en
 *     el check-in, para que no haya una puerta más blanda que la otra.
 *   - EL SALTO. Más de 4 kg de diferencia con el pesaje anterior puede ser verdad -- alguien
 *     que vuelve después de un parón -- así que aquí no se bloquea: se pregunta. Confirmar y
 *     seguir, o corregir. Confirmado, el peso SE TOMA SIEMPRE (Enrique, 29-10): el servidor
 *     ya no descarta saltos.
 *
 * `revisarPeso` no pinta nada ni decide cómo se pregunta: devuelve qué pasa y con qué
 * frase, y cada pantalla lo enseña con lo que ya usa (toast para el error, el diálogo de
 * confirmación para el salto).
 */

export const PESO_MIN = 25;
export const PESO_MAX = 300;
// Por encima de esto se pregunta. Eran 10 kg; desde el 29-10 son los 4 kg de la regla de los
// saltos del servidor, que dejó de descartar pesajes y pasa a ser solo este aviso (Enrique).
export const SALTO_QUE_CANTA = 4;

// Coma decimal, y sin decimales cuando son cero: «75 kg», «77,1 kg».
export const kg = (n) => {
    const v = Math.round(Number(n) * 10) / 10;
    return Number.isFinite(v) ? String(v).replace('.', ',') : '';
};

/**
 * @param {*} valor    lo que ha escrito el cliente (texto o número)
 * @param {*} anterior su último peso conocido, si lo hay
 * @param {{ajeno?: boolean}} [opciones] `ajeno`: lo escribe el equipo en la ficha de un cliente
 * @returns {{ok: boolean, peso?: number, error?: string, confirmar?: string}}
 *          `error` = no se guarda. `confirmar` = se guarda si él lo confirma.
 */
export function revisarPeso(valor, anterior = null, { ajeno = false } = {}) {
    // `ajeno`: quien escribe es el equipo y el peso es del cliente (MAC-22, 14-10). En la
    // ficha se leía «Escribe tu peso» y «Tu último peso fue...», dicho a quien no es.
    const peso = Number(String(valor ?? '').replace(',', '.'));
    if (!Number.isFinite(peso) || peso <= 0) {
        return { ok: false, error: `Escribe ${ajeno ? 'el' : 'tu'} peso en kilos, por ejemplo 78,4.` };
    }
    if (peso < PESO_MIN || peso > PESO_MAX) {
        return {
            ok: false,
            error: `${kg(peso)} kg no parece un peso. Ponlo en kilos, entre ${PESO_MIN} y ${PESO_MAX}.`,
        };
    }
    const previo = Number(anterior);
    if (Number.isFinite(previo) && previo > 0) {
        const salto = Math.abs(peso - previo);
        if (salto > SALTO_QUE_CANTA) {
            return {
                ok: true,
                peso,
                confirmar: `${ajeno ? 'Su' : 'Tu'} último peso fue ${kg(previo)} kg y ahora pones ${kg(peso)} kg: `
                    + `son ${kg(salto)} kg de diferencia. ¿Está bien?`,
            };
        }
    }
    return { ok: true, peso, confirmar: null };
}

/**
 * DE QUÉ DÍA ES EL PESO QUE EL EQUIPO GUARDA CON UN AJUSTE (14-10, REP-10).
 *
 * El campo «Peso» de la ficha llega con el del último reporte y se archiva con la fecha de
 * ese reporte, que es el día en que el cliente se pesó. Pero si el equipo lo cambia a mano,
 * ese número ya no es el del reporte: se guardaba igual «del reporte del 13/10» y en el
 * historial quedaba un peso que el cliente nunca dio. Solo lleva la fecha del reporte el
 * peso que coincide con el del reporte; cualquier otro va sin ella y se archiva con la fecha
 * del ajuste, como cuando no hay reporte.
 *
 * @param {*} escrito      lo que hay en el campo
 * @param {*} delReporte   el peso del reporte que se está ajustando (o nada)
 * @param {string} fechaDelReporte  su día, AAAA-MM-DD
 * @returns {string} la fecha del pesaje, o '' si el peso no sale del reporte
 */
export function fechaDelPesaje(escrito, delReporte, fechaDelReporte) {
    const a = Number(String(escrito ?? '').replace(',', '.'));
    const b = Number(delReporte);
    if (!fechaDelReporte || escrito === '' || escrito == null || delReporte == null) return '';
    return Number.isFinite(a) && Number.isFinite(b) && a === b ? fechaDelReporte : '';
}
